// LoginPage — "Cinematic Split": warmes Vollbild-Foto + Einladungskarte

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E")`;

const InvitationCard = ({ children }) => (
  <div
    className="p-2 overflow-hidden w-full"
    style={{ backgroundColor: "#FFFCF9", borderRadius: 4, border: "1px solid rgba(224,122,95,0.35)", boxShadow: "0 12px 32px rgba(82,42,37,0.08)" }}
  >
    <div className="px-7 py-8 sm:px-9" style={{ border: "1px solid rgba(224,122,95,0.3)", borderRadius: 2 }}>
      {children}
    </div>
  </div>
);

const UnderlineField = ({ label, testId, type = "text", value, onChange, autoComplete }) => (
  <div>
    <label className="block text-[11px] uppercase font-semibold mb-0" style={{ letterSpacing: "0.18em", color: "#6B6157" }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      data-testid={testId}
      required
      autoComplete={autoComplete}
      className="dh-underline-input w-full font-heading text-[16px] pt-1 pb-1"
      style={{ color: "#1B1A18" }}
    />
  </div>
);

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const result = await login(username, password);
    setLoading(false);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError("Das hat nicht geklappt — prüf bitte Benutzername und Passwort.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ backgroundColor: "#F9E7DC" }}>
      {/* IMAGE PANEL — cinematic Ken Burns */}
      <div className="relative w-full h-[45vh] md:h-screen md:w-[55%] overflow-hidden dh-login-item" style={{ animationDelay: "0ms" }}>
        <img
          src="/login_hero_bright.jpg"
          alt="Lernen im Morgenlicht — Dickies-Campus Campus"
          className="absolute inset-0 w-full h-full object-cover dh-kenburns"
          style={{ objectPosition: "center 30%" }}
        />
        {/* Soft warm gradient from bottom for text readability */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(58,42,34,0.62) 0%, rgba(58,42,34,0.22) 30%, transparent 52%)" }}
          aria-hidden
        />
        <div className="absolute bottom-7 left-7 md:bottom-12 md:left-12 right-7 z-10">
          <p className="text-[11px] uppercase font-semibold mb-3" style={{ letterSpacing: "0.3em", color: "#FAD7C4" }}>
            Akademie für Pflege · Campus · MMXXVI
          </p>
          <h2 className="font-display text-[28px] md:text-[38px]" style={{ color: "#FFF9F4", lineHeight: 1.15 }}>
            <span className="hidden md:inline">
              Dein Wissen wächst —<br />
              <span className="italic" style={{ color: "#FAD7C4" }}>jeden Tag ein Kapitel</span>.
            </span>
            <span className="md:hidden">
              Jeden Tag <span className="italic" style={{ color: "#FAD7C4" }}>ein Kapitel</span>.
            </span>
          </h2>
          <div className="hidden md:block mt-5" style={{ width: 60, height: 1, backgroundColor: "#E07A5F" }} />
          <p className="hidden md:block font-display italic mt-4 text-[14px]" style={{ color: "rgba(255,249,244,0.9)" }}>
            Über 1.400 Pflegekräfte lernen bereits hier.
          </p>
        </div>
      </div>

      {/* FORM PANEL — espresso + grain */}
      <div className="relative flex-1 md:w-[45%] flex flex-col items-center justify-center px-3 sm:px-8 py-10 md:py-12">
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.05, backgroundImage: GRAIN }} aria-hidden />

        {/* Wordmark */}
        <div className="relative z-10 text-center mb-8 dh-login-item" style={{ animationDelay: "150ms" }}>
          <div className="flex justify-center mb-3">
            <img src="/dh_logo_terra.png" alt="Dickies-Campus" className="w-14 h-14 rounded-xl object-contain" />
          </div>
          <h1 className="font-display text-3xl" style={{ color: "#522A25" }}>
            Dickies-<span className="italic" style={{ color: "#E07A5F" }}>Campus</span>
          </h1>
        </div>

        {/* Invitation card */}
        <div className="relative z-10 w-full max-w-[420px] dh-login-item" style={{ animationDelay: "300ms" }}>
          <InvitationCard>
            <div className="text-[11px] uppercase font-bold mb-2" style={{ letterSpacing: "0.22em", color: "#E07A5F" }}>
              Willkommen zurück
            </div>
            <h2 className="font-display text-[30px] leading-snug mb-10" style={{ color: "#1B1A18" }}>
              Schön, dass du <span className="italic whitespace-nowrap" style={{ color: "#E07A5F" }}>da&nbsp;bist</span>.
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="space-y-9">
                <UnderlineField
                  label="Benutzername"
                  testId="login-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                />
                <UnderlineField
                  label="Passwort"
                  testId="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="mt-11 mx-auto block w-[60%] h-[52px] rounded-full bg-transparent text-[13px] font-bold uppercase tracking-[0.2em] disabled:opacity-50 dh-invite-btn"
              >
                <span className="flex items-center justify-center gap-2">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Anmelden
                </span>
              </button>

              {error && (
                <p className="mt-5 text-sm text-center leading-relaxed" style={{ color: "#E07A5F" }} data-testid="login-error">
                  {error}
                </p>
              )}
            </form>

            <p className="font-display italic text-center mt-7 text-[15px]" style={{ color: "#6B6157" }}>
              Probleme beim Login? Sprich deine Kursleitung an.
            </p>
          </InvitationCard>
        </div>

        <p
          className="relative z-10 mt-7 text-xs dh-login-item"
          style={{ animationDelay: "450ms", color: "rgba(82,42,37,0.55)" }}
        >
          Jeden <span style={{ color: "#C4614A" }}>Montag</span> startklar · Support Mo–Fr 8–18 Uhr
          <span className="mx-2" aria-hidden>·</span>
          <a href="/barrierefreiheit" className="hover:underline underline-offset-4" style={{ color: "rgba(82,42,37,0.7)" }} data-testid="accessibility-footer-link">
            Barrierefreiheit
          </a>
        </p>
      </div>
    </div>
  );
};

// Hidden setup page — only reachable via direct URL /setup
export const SetupPage = () => {
  const [seeding, setSeeding] = useState(false);
  const [result, setResult] = useState(null);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/seed`);
      toast.success(response.data.message);
      if (response.data.admin_user) {
        setResult({ user: response.data.admin_user, password: response.data.admin_password });
      }
    } catch (error) {
      toast.error("Fehler beim Initialisieren");
    }
    setSeeding(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center px-3 sm:px-6 py-12" style={{ backgroundColor: "#F9E7DC" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.05, backgroundImage: GRAIN }} aria-hidden />
      <div className="relative z-10 text-center mb-8 dh-login-item">
        <div className="flex justify-center mb-3">
          <img src="/dh_logo_terra.png" alt="Dickies-Campus" className="w-14 h-14 rounded-xl object-contain" />
        </div>
        <h1 className="font-display text-3xl" style={{ color: "#522A25" }}>Dickies-<span className="italic" style={{ color: "#E07A5F" }}>Campus</span></h1>
      </div>
      <div className="relative z-10 w-full max-w-[420px] dh-login-item" style={{ animationDelay: "150ms" }}>
        <InvitationCard>
          <div className="text-center">
            <h2 className="font-display text-2xl mb-6" style={{ color: "#1B1A18" }}>
              System-<span className="italic" style={{ color: "#E07A5F" }}>Einrichtung</span>
            </h2>
            <button
              onClick={handleSeed}
              disabled={seeding}
              data-testid="seed-button"
              className="mx-auto block w-[80%] h-[52px] rounded-full bg-transparent text-[13px] font-bold uppercase tracking-[0.2em] disabled:opacity-50 dh-invite-btn"
            >
              <span className="flex items-center justify-center gap-2">
                {seeding && <Loader2 className="w-4 h-4 animate-spin" />}
                Datenbank initialisieren
              </span>
            </button>
            {result && (
              <p className="mt-6 text-sm" style={{ color: "#6B6157" }}>
                Admin: <strong>{result.user}</strong> · Passwort: <strong>{result.password}</strong>
              </p>
            )}
          </div>
        </InvitationCard>
      </div>
    </div>
  );
};

export default LoginPage;
